import { KonvaEventObject } from "konva/lib/Node";
import React, { memo, useCallback, useRef, useState } from "react";
import {
  Stage,
  Layer,
  Text as KonvaText,
  Rect as KonvaRect,
  Image as KonvaImage,
  Circle as KonvaCircle,
  Line as KonvaLine,
  Transformer,
} from "react-konva";
import { v4 as uuidv4 } from "uuid";
import { Circle, Rectangle, Tex } from "./Paint.types";
import { DrawAction, PAINT_OPTIONS } from "./Paint.constants";
import { SketchPicker } from "react-color";
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
} from "@chakra-ui/react";

import { Upload, XLg, Floppy2Fill } from "react-bootstrap-icons";

interface PaintProps {
  data: any;
  control: boolean;
  visible: boolean;
  onUpdate: (data: any) => void;
}

const downloadURI = (uri: string | undefined, name: string) => {
  const link = document.createElement("a");
  link.download = name;
  link.href = uri || "";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
import TextEditor from "./TextEditor";
import { uncachedUrl } from "../../../../shared/utility";
import { edit } from "../../../../assets/images/icons";

const SIZE = 2000;
export const Paint = memo((props: PaintProps) => {

  const { control, visible, onUpdate = () => { }, data } = props;

  const [color, setColor] = useState("black");
  const [drawAction, setDrawAction] = useState<DrawAction>(control ? DrawAction.Select : undefined);
  const [image, setImage] = useState('');
  const [images, setImages] = useState<Tex[]>(data?.images ?? []);
  const [textArr, setTextArr] = useState<Tex[]>(data?.textArr ?? []);
  const [textFont, setTextFont] = useState('Nunito Sans');

  const [isEdit, setIsEdit] = useState(false);
  const [targetEdit, setTargetEdit] = useState<Tex>();
  const [targetImg, setTargetImg] = useState<Tex>();



  const fileRef = useRef<HTMLInputElement>(null);
  const stageRef = useRef<any>(null);

  const onAddImageClick = useCallback(() => {
    if (image) {
      const id = uuidv4();
      setImages((prevImages) => [
        ...prevImages,
        {
          id,
          text: image,
          color,
          visible,
          x: 0,
          y: 0,
          width: 300,
          height: 100,
          size: 20,
          font: textFont,

        }
      ]
      );
    }

  }, [image])

  const onClear = useCallback(() => {

    setTextArr((arr) => arr.filter(tx => tx.id !== targetEdit?.id));
    setImages((arr) => arr.filter(tx => tx.id !== targetImg?.id));
    transformerRef?.current?.nodes([]);
    setIsEdit(false);
  }, [targetEdit, targetImg]);

  const isPaintRef = useRef(false);
  const textRef = useRef<any>([]);
  const imgRef = useRef<any>([]);

  const onStageMouseUp = useCallback(() => {
    isPaintRef.current = false;
  }, []);

  const currentShapeRef = useRef<string>();

  const onStageMouseDown = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      if (drawAction === DrawAction.Select) return;
      isPaintRef.current = true;
      const stage = stageRef?.current;
      const pos = stage?.getPointerPosition();
      const x = pos?.x || 0;
      const y = pos?.y || 0;
      const id = uuidv4();
      const visible = true;
      currentShapeRef.current = id;

      switch (drawAction) {

        case DrawAction.Text: {
          setTextArr((prevTexts) => [
            ...prevTexts,
            {
              id,
              text: 'Your text here',
              color,
              visible,
              x: 0,
              y: 0,
              width: 300,
              height: 0,
              size: 20,
              font: textFont,

            }
          ]
          );
          setDrawAction(DrawAction.Select);

          break;
        }
      }
    },
    [drawAction, color]
  );


  const transformerRef = useRef<any>(null);

  const onShapeClick = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      if (drawAction !== DrawAction.Select) return;
      const currentTarget = e.currentTarget;
      transformerRef?.current?.node(currentTarget);
      const found = textArr.find(tx => tx.id === currentTarget?.attrs?.id);
      if (found) {
        setTargetEdit(found);
        setTargetImg(undefined);
      } else {
        setTargetImg(images.find(tx => tx.id === currentTarget?.attrs?.id));
        setTargetEdit(undefined);
      }
    },
    [drawAction]
  );

  const handleTextDblClick = useCallback((i: string) => {
    const found = textArr.find(tx => tx.id === i);
    if (found) {
      found.visible = false;
      setTargetEdit(found);
      setIsEdit(true);
    }
  }, [textArr]);

  const handleTextChange = useCallback((newText: string) => {
    const found = textArr.find(tx => tx.id === targetEdit?.id);
    if (found) {
      found.text = newText;
    }

  }, [targetEdit]);

  const isDraggable = drawAction === DrawAction.Select;

  const onBgClick = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      transformerRef?.current?.nodes([]);
    },
    [drawAction]
  );

  const handleTransform = useCallback((e) => {
    const found = textArr.findIndex(tx => tx.id === e);
    const node = textRef.current[found];
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const newWidth = node.width() * scaleX;

    const newFontSize = node.fontSize() * scaleY;

    textArr[found].width = newWidth;
    textArr[found].size = newFontSize;
    textArr[found].x = node.x();
    textArr[found].y = node.y();

    node.setAttrs({
      width: newWidth,
      scaleX: 1,
      scaleY: 1,
      fontSize: newFontSize,
    });
    setTextArr(textArr);
  }, [textArr]);

  const handleImgTransform = useCallback((e) => {
    const found = images.findIndex(tx => tx.id === e);
    const node = imgRef.current[found];
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const newWidth = node.width() * scaleX;
    const newHeight = node.height() * scaleY;

    images[found].width = newWidth;
    images[found].height = newHeight;

    images[found].x = node.x();
    images[found].y = node.y();

    node.setAttrs({
      width: newWidth,
      height: newHeight,
      scaleX: 1,
      scaleY: 1,
    });
    setImages(images);
  }, [images]);

  const handleDragImgMove = useCallback((e) => {

    const found = images.findIndex(tx => tx.id === e);
    const node = imgRef.current[found];
    images[found].x = node.x();
    images[found].y = node.y();

    setImages(images);


  }, [images, imgRef]);

  const handleDragMove = useCallback((e) => {
    const found = textArr.findIndex(tx => tx.id === e);

    const node = textRef.current[found];

    textArr[found].x = node.x();
    textArr[found].y = node.y();

    setTextArr(textArr);


  }, [textArr, textRef]);

  return (
    visible && <Box m={4} width={`auto`}>
      {control && <Flex justifyContent={"space-between"} alignItems="center">
        <ButtonGroup size="sm" isAttached variant="solid">
          {PAINT_OPTIONS.map(({ id, label, icon }) => (
            <IconButton
              key={id}
              aria-label={label}
              icon={icon}
              onClick={() => setDrawAction(id)}
              colorScheme={id === drawAction ? "whatsapp" : undefined}
            >
            </IconButton>
          ))}

          <select id="fontSelector" onChange={e => setTextFont(e.target.value)}>
            <option value='Nunito Sans'>{`Nunito Sans`}</option>
            <option value='Montserrat'>{`Montserrat`}</option>
            <option value="Arial">Arial</option>
            <option value="Verdana">Verdana</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Courier New">Courier New</option>
            <option value="Georgia">Georgia</option>
          </select>

          <Popover>
            <PopoverTrigger>
              <Box
                border={'1px solid black'}
                bg={color}
                h={"32px"}
                w={"32px"}
                borderRadius="sm"
                cursor="pointer"
              ></Box>
            </PopoverTrigger>
            <PopoverContent width="300">
              <PopoverArrow />
              <PopoverCloseButton />
              <SketchPicker
                color={color}
                onChangeComplete={(selectedColor) =>
                  setColor(selectedColor.hex)
                }
              />

            </PopoverContent>
          </Popover>
          <IconButton aria-label={"Clear"} icon={<XLg size={20} />} onClick={onClear} />
          <IconButton aria-label={"Save"} icon={<Floppy2Fill size={20} />} onClick={() =>
            onUpdate({
              textArr: textArr,
              images: images
            })
          } />
        </ButtonGroup>
        <Flex gap={4} alignItems="center" height="100%" style={{ display: '' }}>
          <input
            type="text"
            style={{ width: '400px', border: '1px solid black', padding: '10px' }}
            onBlur={e => setImage(e.target.value)}
          />
          <Button
            leftIcon={<Upload />}
            variant="solid"
            onClick={onAddImageClick}
            size="sm"
          >
            {'Add Image'}
          </Button>


        </Flex>
      </Flex>
      }
      <Box
        width={window.innerWidth}
        height={window.innerHeight}
        border={control ? "1px solid black" : 'none'}
        backgroundColor={'transparent'}
        mt={4}

      >
        <Stage
          width={window.innerWidth}
          height={window.innerHeight}
          ref={stageRef}
          onMouseUp={onStageMouseUp}
          onMouseDown={onStageMouseDown}

        >
          <Layer>
            <KonvaRect
              x={0}
              y={0}
              height={SIZE}
              width={SIZE}
              fill="transparent"
              id="bg"
              onClick={onBgClick}
            />

            {images.map((img, i) => {
              const newImg = new Image();
              newImg.src = img.text;

              return (<KonvaImage
                key={img.id}
                id={img.id}
                ref={(el) => {
                  if (el) {
                    imgRef.current[i] = el;
                  }
                }}
                x={+img.x}
                y={+img.y}
                image={newImg}
                height={+(img?.height ?? SIZE / 3)}
                width={+(img?.width ?? SIZE / 2)}
                onClick={onShapeClick}
                onTransform={() => handleImgTransform(img.id)}
                onDragMove={() => handleDragImgMove(img.id)}
                draggable={isDraggable}
              />)
            })}
            {textArr.map((t, i) => {
              return (
                <KonvaText
                  key={t.id}
                  id={t.id}
                  ref={(el) => {
                    if (el) {
                      textRef.current[i] = el;
                    }
                  }}
                  x={+t.x}
                  y={+t.y}
                  text={t.text}
                  width={+t.width}
                  fontSize={+t.size}
                  fontFamily={t.font}
                  fill={t.color}
                  onClick={onShapeClick}
                  onDblClick={() => handleTextDblClick(t.id)}
                  onTransform={() => handleTransform(t.id)}
                  onDragMove={() => handleDragMove(t.id)}
                  draggable={isDraggable}
                  visible={t.visible}
                />)

            }
            )}
            {isEdit && (
              <TextEditor
                textNode={textRef.current[textArr.findIndex(t => t.id === targetEdit?.id)]}
                onChange={handleTextChange}
                onClose={() => {

                  const found = textArr.findIndex(tx => tx.id === targetEdit?.id);
                  textArr[found].visible = true;
                  setTextArr(textArr);
                  setIsEdit(false);
                }}
              />
            )}




            {control && <KonvaRect
              x={window.innerWidth - 230}
              y={10}
              height={40}
              width={100}
              fill={'white'}
              stroke={'black'}
              strokeWidth={1}
            />}
            {control && <KonvaRect

              x={window.innerWidth - 120}
              y={10}
              height={40}
              width={110}
              fill={'white'}
              stroke={'black'}
              strokeWidth={1}
            />}

            {!isEdit && <Transformer ref={transformerRef} />}

          </Layer>

        </Stage>
        {props.children}
      </Box>
    </Box>
  );
});
